import { backend } from "@/services/backend";
import { ratingBackend } from "@/services/ratingBackend";
import {
  fetchData as fetchJson,
  CollectionStandardData,
} from "@/types/collection";
import { LeaderboardItemType, LeaderboardMetric } from "@/types/leader-board";
import { calcLevelByExperience } from "@/utils/experience";
import {
  LEADERBOARD_CONSTANTS,
  LEADERBOARD_TEXT,
} from "@/constants/leaderboard";

export async function fetchOwnerNftData(
  ownerAddress: string
): Promise<LeaderboardItemType | null> {
  try {
    const nftData = await ratingBackend.getOwnerBestNft({
      owner: ownerAddress,
      param: "stars",
    });

    if (!nftData) return null;

    const meta = await fetchJson(nftData.dataUrl);
    return {
      rank: nftData.rang,
      name: meta.name,
      value: Number(nftData.stars || 0),
      address: nftData.address,
      avatar: meta.image,
      indexInCollection: nftData.indexInCollection,
      ownerAddress: ownerAddress,
    };
  } catch (error) {
    return null;
  }
}

export async function fetchOwnerCollectionData(
  ownerAddress: string,
  metric: LeaderboardMetric
): Promise<LeaderboardItemType | null> {
  try {
    const param = metric === "volume" ? "volume" : "stars";
    const collectionData = await ratingBackend.getOwnerBestCollection({
      owner: ownerAddress,
      param,
    });

    if (!collectionData) return null;

    const meta: CollectionStandardData = await fetchJson(
      collectionData.dataUrl
    );
    return {
      rank:
        metric === "volume"
          ? collectionData.rangVolume || 0
          : collectionData.rangStars || 0,
      name: meta.name || LEADERBOARD_TEXT.UNKNOWN_NAME,
      value:
        metric === "volume"
          ? Number(collectionData.volume || 0) /
            LEADERBOARD_CONSTANTS.TON_DIVISOR
          : Number(collectionData.stars || 0),
      address: collectionData.address,
      avatar: meta.image || LEADERBOARD_CONSTANTS.DEFAULT_AVATAR,
      ownerAddress: ownerAddress,
    };
  } catch (error) {
    return null;
  }
}

export async function fetchOwnerUserData(
  ownerAddress: string,
  metric: LeaderboardMetric
): Promise<LeaderboardItemType | null> {
  const ownerRatingData = await ratingBackend.getOwnerRatingData(ownerAddress);

  if (!ownerRatingData) return null;

  let value: number;
  let rankParam: "volume" | "stars" | "experience";

  switch (metric) {
    case "volume":
      value =
        Number(ownerRatingData.volume || 0) / LEADERBOARD_CONSTANTS.TON_DIVISOR;
      rankParam = "volume";
      break;
    case "level":
      value = calcLevelByExperience(ownerRatingData.experience);
      rankParam = "experience";
      break;
    case "all_stars":
    default:
      value = Math.floor(Number(ownerRatingData.stars || 0));
      rankParam = "stars";
      break;
  }

  const rank = await backend.getWalletRatingPosition(ownerAddress, rankParam);

  return {
    rank: rank || 0,
    name: ownerRatingData.username
      ? ownerRatingData.username
      : `USER#${ownerAddress}`,
    value,
    address: ownerAddress,
    avatar: ownerRatingData.avatar || LEADERBOARD_CONSTANTS.DEFAULT_AVATAR,
    ownerAddress: ownerAddress,
  };
}

// Fetch collection data by collection address (for share links)
export async function fetchCollectionDataByAddress(
  collectionAddress: string,
  metric: LeaderboardMetric
): Promise<LeaderboardItemType | null> {
  try {
    const collectionData = await ratingBackend.getSingleCollectionRatingData({
      address: collectionAddress,
    });

    if (!collectionData) return null;

    const meta: CollectionStandardData = await fetchJson(
      collectionData.dataUrl
    );
    return {
      rank:
        metric === "volume"
          ? collectionData.rangVolume || 0
          : collectionData.rangStars || 0,
      name: meta.name || LEADERBOARD_TEXT.UNKNOWN_NAME,
      value:
        metric === "volume"
          ? Number(collectionData.volume || 0) /
            LEADERBOARD_CONSTANTS.TON_DIVISOR
          : Number(collectionData.stars || 0),
      address: collectionData.address,
      avatar: meta.image || LEADERBOARD_CONSTANTS.DEFAULT_AVATAR,
      ownerAddress: collectionData.owner || "",
    };
  } catch (error) {
    console.error("Error fetching collection data by address:", error);
    return null;
  }
}

// Fetch NFT data by NFT address (for share links)
export async function fetchNftDataByAddress(
  nftAddress: string
): Promise<LeaderboardItemType | null> {
  try {
    const nftData = await ratingBackend.getNftRatingData({
      address: nftAddress,
      param: "stars",
    });

    if (!nftData) return null;

    const meta = await fetchJson(nftData.dataUrl);
    return {
      rank: nftData.rang,
      name: meta.name || LEADERBOARD_TEXT.UNKNOWN_NAME,
      value: Number(nftData.stars || 0),
      address: nftData.address,
      avatar: meta.image || LEADERBOARD_CONSTANTS.DEFAULT_AVATAR,
      indexInCollection: nftData.indexInCollection,
      ownerAddress: nftData.owner || "",
    };
  } catch (error) {
    console.error("Error fetching NFT data by address:", error);
    return null;
  }
}
